/**
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; under version 2
 * of the License (non-upgradable).
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
 *
 * Copyright (c) 2020-2024 (original work) Open Assessment Technologies SA ;
 */

/**
 * Given anchor coords and an anchoring description, provides the style definition
 * needed to absolutely position some floating element (like a ModalDialog)
 * @param {Object} options
 * @param {Object} [options.anchorRect] - coords of background element to be used as an anchor point
 * @param {Object} [options.documentDimensions] - { scrollTop, scrollLeft, clientWidth, clientHeight }
 * @param {String} [options.anchoring='top left'] - which corner of the foreground element should be anchored to it
 * @param {Object} [options.offset] - { x, y } extra visual offset to apply
 * @returns {Object} with top, bottom, left, right for CSS
 */
export function calculateAbsolutePosition(
    options = {
        anchorRect: { top: 0, left: 0, bottom: 0, right: 0 },
        documentDimensions: { scrollTop: 0, scrollLeft: 0, clientWidth: 0, clientHeight: 0 },
        anchoring: 'top left',
        offset: {
            x: 0,
            y: 0
        }
    }
) {
    const { scrollTop, scrollLeft, clientWidth, clientHeight } = options.documentDimensions;
    const { anchorRect, offset } = options;

    const anchorKeys = options.anchoring.split(' ');

    // CSS properties
    let top, bottom, left, right;

    // Calculate absolute position
    // Vertical
    if (anchorKeys[0] === 'top') {
        top = `${scrollTop + anchorRect.bottom + offset.y}px`;
        bottom = 'unset';
    } else if (anchorKeys[0] === 'bottom') {
        bottom = `${-scrollTop + clientHeight + offset.y - anchorRect.top}px`;
        top = 'unset';
    }

    // Horizontal
    if (anchorKeys[1] === 'left') {
        left = `${scrollLeft + anchorRect.left + offset.x}px`;
        right = 'unset';
    } else if (anchorKeys[1] === 'right') {
        right = `${-scrollLeft + clientWidth + offset.x - anchorRect.right}px`;
        left = 'unset';
    }

    return {
        top,
        bottom,
        left,
        right
    };
}

/**
 * Gets the dimensions of the nearest positioned container, or the document element.
 * These dimensions are represented by the scroll coordinates and the client size.
 *
 * Note: when using absolute positioning, the coordinates may still be relative
 * to the nearest positioned parent. In this case, we need to adjust the scroll
 * coordinates so that they reflect the position relative to this parent. This
 * gymnastic is required when using the helper `calculateAbsolutePosition()`,
 * since it relies on document dimensions, a.k.a the nearest positioned parent,
 * to compute the final coordinates.
 *
 * @param {HTMLElement} element - The element for which get the parent dimensions.
 * @returns {object} - An object containing the scroll coordinates and the client size.
 */
export function getParentDimensions(element = null) {
    const getDimensions = parent => {
        let { scrollTop, scrollLeft, clientWidth, clientHeight } = parent;
        return { scrollTop, scrollLeft, clientWidth, clientHeight };
    };

    // Get the nearest positioned container.
    const offsetParent = element && element.offsetParent;
    if (!offsetParent) {
        return getDimensions(document.documentElement);
    }

    // Adjust the scroll coordinates to reflect the possible offset from the top left corner,
    // if the parent is not the document.
    const rect = offsetParent.getBoundingClientRect();
    const dimensions = getDimensions(offsetParent);
    dimensions.scrollTop -= rect.top;
    dimensions.scrollLeft -= rect.left;
    return dimensions;
}
